import { useUnit } from 'effector-react';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $charts,
  $layout,
  DEFAULT_PERIOD,
  DEFAULT_STYLE,
  MAX_CHARTS,
  lookupSymbolFx,
  type ChartItem,
} from '../../entities/chart';
import {
  Button,
  Dialog,
  DialogContent,
  Field,
  IconButton,
  Input,
  SortableList,
  Tooltip,
} from '../../shared/ui';
import { $open, $saveError, closed, saveSymbolsFx } from './model';

export function ChartSymbolsDialog() {
  const [open, close] = useUnit([$open, closed]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && <Form />}
    </Dialog>
  );
}

function Form() {
  const { t } = useTranslation();
  const [layout, charts, saveError, saving, looking, close, save, lookup] = useUnit([
    $layout,
    $charts,
    $saveError,
    saveSymbolsFx.pending,
    lookupSymbolFx.pending,
    closed,
    saveSymbolsFx,
    lookupSymbolFx,
  ]);
  // A local draft: nothing changes on the dashboard until Save.
  const [items, setItems] = useState<ChartItem[]>(() => layout ?? []);
  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(charts).flatMap(([symbol, state]) =>
        state.data?.name ? [[symbol, state.data.name]] : [],
      ),
    ),
  );
  const [symbol, setSymbol] = useState('');
  const [addError, setAddError] = useState<string | undefined>();
  const [dragging, setDragging] = useState(false);
  const full = items.length >= MAX_CHARTS;

  function add(): void {
    const wanted = symbol.trim().toUpperCase();
    if (!wanted) return;
    if (items.some((i) => i.symbol === wanted)) {
      setAddError(t('charts.duplicate', { symbol: wanted }));
      return;
    }
    setAddError(undefined);
    lookup(wanted)
      .then((found) => {
        setItems((current) =>
          current.some((i) => i.symbol === found.symbol)
            ? current
            : [...current, { symbol: found.symbol, period: DEFAULT_PERIOD, style: DEFAULT_STYLE }],
        );
        if (found.name) setNames((current) => ({ ...current, [found.symbol]: found.name ?? '' }));
        setSymbol('');
      })
      .catch((error: unknown) => {
        setAddError(error instanceof Error ? error.message : String(error));
      });
  }

  return (
    <DialogContent
      title={t('charts.editTitle')}
      description={t('charts.editDescription')}
      closeLabel={t('common.close')}
      onSubmit={() => {
        void save(items).catch(() => undefined);
      }}
      onEscapeKeyDown={(event) => {
        // Esc first cancels a move in progress.
        if (dragging) event.preventDefault();
      }}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={t('charts.symbol')}
          hint={
            full
              ? t('charts.full', { max: MAX_CHARTS })
              : t('charts.count', { count: items.length, max: MAX_CHARTS })
          }
          error={addError}
        >
          {({ id, describedBy, invalid }) => (
            <div className="flex gap-2">
              <Input
                id={id}
                value={symbol}
                placeholder={t('charts.symbolPlaceholder')}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={full}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                onChange={(event) => {
                  setSymbol(event.target.value);
                  setAddError(undefined);
                }}
                onKeyDown={(event) => {
                  // Enter adds the symbol; ⌘↵ still saves the dialog.
                  if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
                    event.preventDefault();
                    add();
                  }
                }}
              />
              <Button
                leadingIcon={<Plus />}
                disabled={full || looking || !symbol.trim()}
                onClick={add}
              >
                {t('charts.add')}
              </Button>
            </div>
          )}
        </Field>
        {items.length === 0 ? (
          <p className="text-sm text-text-3">{t('charts.emptyList')}</p>
        ) : (
          <SortableList
            label={t('charts.listLabel')}
            items={items}
            getId={(item) => item.symbol}
            onReorder={setItems}
            onDraggingChange={setDragging}
            labels={{
              handle: (id) => t('charts.move', { symbol: id }),
              instructions: t('charts.dragInstructions'),
              pickedUp: (id, position, total) =>
                t('charts.pickedUp', { symbol: id, position, total }),
              movedTo: (id, position, total) =>
                t('charts.movedTo', { symbol: id, position, total }),
              dropped: (id, position, total) =>
                t('charts.dropped', { symbol: id, position, total }),
              cancelled: (id) => t('charts.cancelled', { symbol: id }),
            }}
            renderItem={(item) => {
              const remove = t('charts.remove', { symbol: item.symbol });
              return (
                <>
                  <span className="shrink-0 font-medium">{item.symbol}</span>
                  <span className="min-w-0 flex-1 truncate text-text-3">
                    {names[item.symbol] ?? ''}
                  </span>
                  <Tooltip content={remove}>
                    <IconButton
                      size="sm"
                      label={remove}
                      onClick={() => {
                        setItems((current) => current.filter((i) => i.symbol !== item.symbol));
                      }}
                    >
                      <X />
                    </IconButton>
                  </Tooltip>
                </>
              );
            }}
          />
        )}
        {saveError && (
          <p role="alert" className="text-xs text-negative">
            {saveError}
          </p>
        )}
      </div>
    </DialogContent>
  );
}
